import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
  ChartLine,
  ClipboardList,
  FlaskConical,
  List,
  ListChecks,
  ShieldAlert,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import AppDetailTabs, { TabConfig } from "@/components/layout/AppDetailTabs";
import BorrowerNavHeader from "@/components/application/ApplicationNavHeader";
import { AdminAppStatusFlowbar } from "@/components/admin/AdminAppStatusFlowbar";
import DetailPageSkeleton from "@/components/common/DetailPageSkeleton";
import MutationErrorAlert from "@/components/common/MutationErrorAlert";
import BorrowerProcessingBanners from "@/components/application/BorrowerProcessingBanners";
import ProcessingToastProvider from "@/components/application/ProcessingToast";
import { PlaidProcessingPublisher } from "@/components/application/PlaidStatusBanner";
// Application is the common default and BorrowerForm is already a static
// import. AI Analysis stays static too — funder_review defaults there and
// lazy-loading it would add a chunk-fetch waterfall on that landing.
import AiAnalysisTab from "@/components/application/aiAnalysis/AiAnalysisTab";
import {
  hasAnalysisListLink,
  linkedTab,
  useAnalysisListLink,
  withoutAnalysisListLink,
} from "@/components/application/aiAnalysis/analysisListLink";
import BorrowerForm from "@/pages/broker/BorrowerForm";
import BackgroundCheckTab from "@/components/application/BackgroundCheckTab";
import { NotesButton, NotesButtonPlaceholder } from "@/components/underwriting/NotesButton";
import { useBorrowerDetail } from "@/hooks/useBorrowerDetail";
import IntakeStatusBanner from "@/components/application/IntakeStatusBanner";
import BankVerificationBanner from "@/components/application/BankVerificationBanner";
import { useStatements } from "@/hooks/useStatements";
import { useBorrowerRealtime } from "@/hooks/useBorrowerRealtime";
import { useClaimBorrowerOnOpen } from "@/hooks/useClaimBorrowerOnOpen";
import { usePersistentTab } from "@/hooks/usePersistentTab";
import { useExperimentalFeatures } from "@/hooks/useExperimentalFeatures";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAgentPageState } from "@/agent/pageState/registry";
import { useAnalysisRunQuery, useAnalysisRunsQuery } from "@/hooks/queries/useAnalysisRuns";
import {
  mergeLiveAndFetchedProgress,
  statementAnalysisProgressFromRuns,
} from "@/components/application/inFlightAnalysisProgress";
import { isPositionFinderTabVisible } from "./positionFinderVisibility";
import {
  ADMIN_APP_TAB_KEYS,
  BACKGROUND_CHECK_CHIP_STORAGE_KEY,
  BACKGROUND_CHECK_CHIPS,
  defaultAdminAppTabForStatus,
  landingAdminAppTab,
  defaultBackgroundCheckChip,
  readSeededAdminAppTab,
  writeAdminAppDetailTab,
  type AdminAppTabKey,
  type BackgroundCheckChip,
} from "./adminAppDetailTab";
import {
  useDatamerchNotesQuery,
  useRefreshDatamerchNotesMutation,
  useRefreshUnicourtCaseMutation,
  useRefreshUnicourtCasesMutation,
  useUnicourtCasesQuery,
} from "@/hooks/queries";
import { FINAL_UW_TAB_ENABLED_STATUSES, type AppStatus } from "@/types/borrower";
import { lazyWithChunkReload } from "@/utils/lazyWithChunkReload";

// Every other tab is code-split: its JS chunk is only fetched once the user
// actually opens the tab (AppDetailTabs wraps panels in <Suspense>).
const TransactionsTab = lazyWithChunkReload(
  () => import("@/components/application/TransactionsTab")
);
const FinalUnderwritingTab = lazyWithChunkReload(
  () => import("@/components/application/FinalUnderwritingTab")
);
const MerchantTasksTab = lazyWithChunkReload(
  () => import("@/components/application/merchant-tasks/MerchantTasksTab")
);
const DefaultHistory = lazyWithChunkReload(
  () => import("@/components/defaultHistory/DefaultHistory")
);
const BaselayerTab = lazyWithChunkReload(
  () => import("@/components/application/baselayer/BaselayerTab")
);
const PositionFinderTab = lazyWithChunkReload(() =>
  import("@/components/admin/positionFinder/PositionFinderTab").then((m) => ({
    default: m.PositionFinderTab,
  }))
);

const AdminAppDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [multiFunderCreatedCount, setMultiFunderCreatedCount] = useState<number | null>(null);
  const borrowerId = id ? parseInt(id, 10) : null;
  const { borrower, loading, error, refresh } = useBorrowerDetail(borrowerId);

  useEffect(() => {
    const count = (location.state as { multiFunderCreatedCount?: number } | null)
      ?.multiFunderCreatedCount;
    if (typeof count === "number" && count > 1) {
      setMultiFunderCreatedCount(count);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);
  const { user, isUnderwritingActor } = useCurrentUser();
  const [{ positionFinder }] = useExperimentalFeatures();
  const positionFinderTabVisible = isPositionFinderTabVisible(
    user?.role,
    user?.email,
    positionFinder
  );

  const { statements } = useStatements(borrowerId);
  const analysisRunsQuery = useAnalysisRunsQuery(borrowerId);
  const processingStatementRun = analysisRunsQuery.data?.find(
    (run) => run.status === "processing" && run.source === "statements"
  );
  const fetchedStatementProgress = statementAnalysisProgressFromRuns(analysisRunsQuery.data);
  useAnalysisRunQuery(processingStatementRun?.id ?? null, borrowerId);
  // Deferred until the primary borrower fetch resolves so they don't compete
  // with it at mount, while still firing automatically (not gated on the tab).
  const unicourtQuery = useUnicourtCasesQuery(id ?? null, borrower != null);
  const datamerchQuery = useDatamerchNotesQuery(id ?? null, borrower != null);

  const refreshUnicourtCasesMutation = useRefreshUnicourtCasesMutation(borrowerId);
  const refreshDatamerchNotesMutation = useRefreshDatamerchNotesMutation(borrowerId);
  const refreshUnicourtCaseMutation = useRefreshUnicourtCaseMutation(borrowerId);

  useClaimBorrowerOnOpen(borrowerId, borrower);
  const realtime = useBorrowerRealtime(id ?? null);

  // A link's ?tab= (e.g. "Show in application") wins over the landing below.
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlTab = linkedTab(searchParams, ADMIN_APP_TAB_KEYS);
  const analysisListLink = useAnalysisListLink();

  // Session-only: tab clicks are not persisted. Landing uses status defaults
  // (seeded by warmAdminAppDetailTab) or a one-shot deep link (writeAdminAppDetailTab).
  const [activeTab, setActiveTab] = useState<AdminAppTabKey>(
    () => urlTab ?? readSeededAdminAppTab(id) ?? "ai-analysis"
  );
  useAgentPageState("tab", { tab: activeTab });
  const [backgroundChip, setBackgroundChip] = usePersistentTab<BackgroundCheckChip>(
    BACKGROUND_CHECK_CHIP_STORAGE_KEY,
    id,
    defaultBackgroundCheckChip(id),
    BACKGROUND_CHECK_CHIPS
  );
  // Last status we applied a default for. Undefined until borrower loads;
  // reset when the deal id changes so a mid-session status change re-defaults.
  const appliedStatusRef = useRef<{ id: string; status: string } | null>(null);

  const finalUwDisabled =
    !borrower?.app_status ||
    !FINAL_UW_TAB_ENABLED_STATUSES.includes(borrower.app_status as AppStatus);

  // Land on the status default when the deal or its status changes. First paint
  // usually already matches via warmAdminAppDetailTab / writeAdminAppDetailTab.
  useEffect(() => {
    if (!borrower || !id) return;
    const status = borrower.app_status;
    const prev = appliedStatusRef.current;
    if (prev?.id === id && prev.status === status) return;

    const isStatusChange = prev?.id === id && prev.status !== status;
    appliedStatusRef.current = { id, status };

    const statusDefault = defaultAdminAppTabForStatus(status);
    const landing = landingAdminAppTab(status, borrower);

    if (isStatusChange) {
      setActiveTab(landing);
      writeAdminAppDetailTab(id, landing, status);
      return;
    }

    // Keep a one-shot deep link (Edit → Application). A warm write matches the
    // status default, so it can still upgrade to Final Underwriting once data loads.
    const seeded = readSeededAdminAppTab(id, status);
    setActiveTab(urlTab ?? (seeded && seeded !== statusDefault ? seeded : landing));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the URL tab is read at landing; the effect below follows it after
  }, [borrower, id]);

  // A link opened on this page (in place) switches the tab, again each time it is opened.
  useEffect(() => {
    if (urlTab) setActiveTab(urlTab);
  }, [urlTab, location.key]);

  // A tab picked by hand ends the link: its params leave the address, so a reload or a bookmark
  // does not reopen a view that is no longer on screen.
  const changeTab = (tab: AdminAppTabKey) => {
    setActiveTab(tab);
    if (tab !== activeTab && hasAnalysisListLink(searchParams)) {
      navigate(
        { search: withoutAnalysisListLink(searchParams, true).toString() },
        { replace: true, preventScrollReset: true }
      );
    }
  };

  // Escape a tab that just became disabled. Gate on `borrower != null` so
  // we don't bounce off a valid stored tab during the brief window when the
  // borrower query hasn't resolved (the disabled flags default to `true`
  // while borrower is undefined).
  useEffect(() => {
    if (!borrower) return;
    if (activeTab === "final-uw" && finalUwDisabled) setActiveTab("transactions");
    if (activeTab === "position-finder" && !positionFinderTabVisible) {
      setActiveTab("transactions");
    }
  }, [borrower, activeTab, finalUwDisabled, positionFinderTabVisible, setActiveTab]);

  const unicourtCases = unicourtQuery.data?.cases ?? [];
  const datamerchNotes = datamerchQuery.data ?? [];
  // Same records borrower.default_history_count counts; stays until none are left.
  const hasDefaultHistory = unicourtCases.length > 0 || datamerchNotes.length > 0;
  const failedStatementCount = statements.filter((s) => s.status === "failed").length;
  const completedStatementCount = statements.filter((s) => s.status === "completed").length;
  const hasInFlightStatements = statements.some((s) =>
    ["pending", "processing", "uploading"].includes(s.status)
  );
  const allStatementsFailed =
    failedStatementCount > 0 && completedStatementCount === 0 && !hasInFlightStatements;

  const underwritingProgress = mergeLiveAndFetchedProgress(
    realtime.underwritingProgress,
    fetchedStatementProgress
  );

  const isInitialProcessing = useMemo(() => {
    if (allStatementsFailed) return false;
    if (realtime.isInitialProcessingFromWs || fetchedStatementProgress != null) return true;
    if (!borrower) return false;
    const isProcessingStatus =
      borrower.processing_status === "pending" || borrower.processing_status === "processing";
    const hasNoDecisions = !borrower.decisions || borrower.decisions.length === 0;
    const hasStatements = statements.length > 0;
    const hasProcessingStatements = statements.some((s) =>
      ["pending", "processing", "uploading"].includes(s.status)
    );
    return (
      (isProcessingStatus && hasNoDecisions && hasStatements) ||
      (hasProcessingStatements && hasNoDecisions)
    );
  }, [
    allStatementsFailed,
    realtime.isInitialProcessingFromWs,
    fetchedStatementProgress,
    borrower,
    statements,
  ]);

  if (loading && !borrower) {
    return (
      <DetailPageSkeleton showFlowbar={true} tabCount={6} tabActions={<NotesButtonPlaceholder />} />
    );
  }

  if (error) {
    return (
      <div>
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button type="button" variant="outline" onClick={() => navigate("/applications")}>
          <ArrowLeft className="h-4 w-4" />
          Back to Applications
        </Button>
      </div>
    );
  }

  if (!borrower) {
    return (
      <div>
        <Alert className="mb-4 border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
          <AlertDescription>Application not found</AlertDescription>
        </Alert>
        <Button
          type="button"
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/applications")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Applications
        </Button>
      </div>
    );
  }

  const canRefreshDefaultHistory = user != null && user.role !== "broker";

  const tabs: TabConfig[] = [
    {
      key: "application",
      label: "Application",
      icon: <ClipboardList className="h-4 w-4" />,
      panel: <BorrowerForm embedded />,
    },
    {
      key: "default-history",
      label: "Background Check",
      icon: <ShieldAlert className="h-4 w-4" />,
      badge: hasDefaultHistory,
      panel: (
        <BackgroundCheckTab
          chip={backgroundChip}
          onChipChange={setBackgroundChip}
          historyBadge={hasDefaultHistory}
          kyb={<BaselayerTab borrower={borrower} readOnly={!isUnderwritingActor} />}
          history={
            <DefaultHistory
              unicourtCases={unicourtCases}
              datamerchNotes={datamerchNotes}
              unicourtProgress={realtime.unicourtProgress}
              datamerchProgress={realtime.datamerchProgress}
              casesLoading={unicourtQuery.isLoading}
              datamerchLoading={datamerchQuery.isLoading}
              awaitingDocuments={unicourtQuery.data?.awaitingDocuments ?? false}
              awaitingDocumentsCount={unicourtQuery.data?.awaitingDocumentsCount ?? 0}
              unicourtRefreshing={refreshUnicourtCasesMutation.isPending}
              datamerchRefreshing={refreshDatamerchNotesMutation.isPending}
              onRefreshCases={() => {
                refreshUnicourtCasesMutation.mutate();
                refreshDatamerchNotesMutation.mutate();
              }}
              onRefreshCase={(caseId) => refreshUnicourtCaseMutation.mutateAsync(caseId)}
              refreshingCaseId={
                refreshUnicourtCaseMutation.isPending &&
                refreshUnicourtCaseMutation.variables != null
                  ? refreshUnicourtCaseMutation.variables
                  : null
              }
              currentBorrowerCompanyName={borrower.company_name}
              canRefresh={canRefreshDefaultHistory}
            />
          }
        />
      ),
    },
    {
      key: "ai-analysis",
      label: "AI Analysis",
      icon: <ChartLine className="h-4 w-4" />,
      panel:
        borrowerId != null ? (
          <AiAnalysisTab
            borrower={borrower}
            borrowerId={borrowerId}
            readOnly={!isUnderwritingActor}
            listLink={analysisListLink}
          />
        ) : null,
    },
    {
      key: "transactions",
      label: "Transactions",
      icon: <List className="h-4 w-4" />,
      panel: borrowerId != null ? <TransactionsTab borrowerId={borrowerId} /> : null,
    },
    {
      key: "merchant-tasks",
      label: "Merchant Tasks",
      icon: <ListChecks className="h-4 w-4" />,
      panel: <MerchantTasksTab borrower={borrower} />,
    },
    {
      key: "final-uw",
      label: "Final Underwriting",
      icon: <BadgeCheck className="h-4 w-4" />,
      disabled: finalUwDisabled,
      panel: (
        <FinalUnderwritingTab
          borrower={borrower}
          borrowerId={borrowerId}
          allowPlaidControls={isUnderwritingActor}
        />
      ),
    },
    ...(positionFinderTabVisible && borrowerId != null
      ? ([
          {
            key: "position-finder",
            label: "Position finder (exp)",
            icon: <FlaskConical className="h-4 w-4" />,
            panel: <PositionFinderTab borrowerId={borrowerId} statements={statements} />,
          },
        ] as TabConfig[])
      : []),
  ];

  return (
    <div>
      <BorrowerNavHeader
        companyName={borrower.company_name}
        ownerName={borrower.owner_name}
        organizationName={borrower.organization_name}
      />

      {multiFunderCreatedCount != null && (
        <Alert className="mb-3">
          <AlertDescription>
            Created {multiFunderCreatedCount} applications (one per selected funder). You are
            viewing the first; switch organizations to see the others.
          </AlertDescription>
        </Alert>
      )}

      <MutationErrorAlert
        mutations={[
          refreshUnicourtCasesMutation,
          refreshDatamerchNotesMutation,
          refreshUnicourtCaseMutation,
        ]}
      />

      <div className="mb-3">
        <AdminAppStatusFlowbar borrowerId={borrower.id} />
      </div>

      <div className="mb-3">
        <IntakeStatusBanner borrower={borrower} onChanged={refresh} />
      </div>

      <div className="mb-3 empty:hidden">
        <BankVerificationBanner borrower={borrower} canConfirm={isUnderwritingActor} />
      </div>

      <ProcessingToastProvider>
        <BorrowerProcessingBanners
          borrowerId={borrowerId}
          isInitialProcessing={isInitialProcessing}
          failedStatementCount={failedStatementCount}
          allStatementsFailed={allStatementsFailed}
          underwritingProgress={underwritingProgress}
          taskError={realtime.taskError}
          onDismissTaskError={realtime.clearTaskError}
        />
        <PlaidProcessingPublisher borrower={borrower} />
      </ProcessingToastProvider>

      <Card className="mb-6 overflow-hidden p-0 shadow-card">
        <AppDetailTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(v) => changeTab(v as AdminAppTabKey)}
          actions={<NotesButton borrowerId={borrower.id} notes={borrower.application_notes} />}
        />
      </Card>
    </div>
  );
};

export default AdminAppDetail;
